/**
 * CompanyContext.jsx - Track the currently-selected Business Central
 * company (branch) across the whole app.
 *
 * Behaviour:
 *   - Fetches the BC companies list once on mount, via /api/proxy/bc/companies.
 *   - Exposes `companies`, `selectedCompany`, `setSelectedCompany(...)`,
 *     `loading`, `error`.
 *   - Mirrors the selection into bcClient.setCompanyId() so every API
 *     module automatically scopes its OData calls to that company.
 *   - When the user has not picked anything yet, falls back to the env
 *     default (BC_COMPANY_ID) if present, otherwise "All branches"
 *     (no company id = cross-company calls).
 *
 * Local persistence:
 *   - The selected company is saved to localStorage so a page reload
 *     keeps the same branch active.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_BASE } from "../config.js";
import { listCompanies, setCompanyId as setClientCompanyId } from "../api/bcClient.js";
import { useAuth } from "../auth/apiAuth.jsx";

const STORAGE_KEY = "vms.company";

const CompanyContext = createContext(null);

/* Fallback list used when BC hasn't been wired up yet (no client
 * secret). The screenshots show the YSU-Production environment has:
 *   Cody Marketing, JMU Marketing Inc.- Cebu,
 *   JMU Marketing Inc., YSU Marketing Corporation.
 * These names are stable so the user can pick a branch even before
 * /companies is reachable. */
const FALLBACK_COMPANIES = [
  { id: "cody-marketing", name: "Cody Marketing Corporation", displayName: "Cody Marketing Corporation" },
  { id: "jmu-cebu", name: "JMU Marketing Inc.- Cebu", displayName: "JMU Marketing Inc.- Cebu" },
  { id: "jmu", name: "JMU Marketing Inc.", displayName: "JMU Marketing Inc." },
  { id: "ysu", name: "YSU Marketing Corporation", displayName: "YSU Marketing Corporation" },
];

function readSavedCompany() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveCompany(c) {
  if (c) localStorage.setItem(STORAGE_KEY, JSON.stringify(c));
  else localStorage.removeItem(STORAGE_KEY);
}

export function CompanyProvider({ children }) {
  const { isAuthenticated, token } = useAuth();
  const [companies, setCompanies] = useState(FALLBACK_COMPANIES);
  const [selected, setSelectedState] = useState(() => readSavedCompany() || FALLBACK_COMPANIES[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [liveFromBc, setLiveFromBc] = useState(false);

  /* Keep bcClient in sync with the chosen company. */
  useEffect(() => {
    if (selected?.id === "all") {
      // "All branches": scope API calls to the first real company
      const firstCompany = companies.find((c) => c.id !== "all");
      if (firstCompany) {
        setClientCompanyId(firstCompany.id);
      }
    } else if (selected?.id) {
      setClientCompanyId(selected.id);
    }
  }, [selected, companies]);

  /* Fetch companies when the user signs in (or token appears). */
  useEffect(() => {
    if (!isAuthenticated || !token) return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const list = await listCompanies();
        if (cancelled) return;
        if (Array.isArray(list) && list.length) {
          const normalised = list
            .map((c) => ({
              id: c.id || c.Company_Id || c.companyId,
              name: c.displayName || c.Name || c.name,
              displayName: c.displayName || c.Name || c.name,
              evaluationCompany: !!c.evaluationCompany,
            }))
            .filter((c) => c.id);
          if (normalised.length) {
            setCompanies(normalised);
            setLiveFromBc(true);
            // If current selection is a fallback (not from BC), switch to first real company
            const isFallback = selected && !normalised.some((c) => c.id === selected.id);
            if (isFallback || !selected) {
              const firstCompany = normalised[0];
              setSelectedState(firstCompany);
              saveCompany(firstCompany);
              // Update bcClient immediately (synchronously) to avoid race condition
              setClientCompanyId(firstCompany.id);
            }
          }
        }
      } catch (err) {
        // BC not configured (no client secret) or transient failure -
        // we just keep the fallback list so the UI still works.
        if (!cancelled) {
          setError(err?.message || "Could not load companies from BC");
          setLiveFromBc(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, token]);

  /* If the saved selection points to a company that no longer exists,
   * clear it so the auto-select logic can pick the first company.
   * "all" is always valid (shows all branches, uses first company for API). */
  useEffect(() => {
    if (!selected) return;
    if (selected.id === "all") return; // "all" is always valid
    const stillExists = companies.some((c) => c.id === selected.id);
    if (!stillExists) {
      setSelectedState(null);
      saveCompany(null);
    }
  }, [companies, selected]);

  const setSelectedCompany = useCallback((company) => {
    const next = company;
    setSelectedState(next);
    saveCompany(next);
    // Immediately set bcClient company ID — don't defer to useEffect
    if (next?.id === "all") {
      // "All branches": use first real company's GUID for API scope
      const firstReal = companies.find((c) => c.id !== "all");
      if (firstReal) setClientCompanyId(firstReal.id);
    } else if (next?.id) {
      setClientCompanyId(next.id);
    }
  }, [companies]);

  const clearSelection = useCallback(() => {
    setSelectedState(null);
    saveCompany(null);
  }, []);

  const value = useMemo(
    () => ({
      companies,
      selectedCompany: selected,
      setSelectedCompany,
      clearSelection,
      loading,
      error,
      liveFromBc,
      // Helper for views that want to display "All branches" explicitly.
      allBranchesOption: { id: "all", name: "All branches", displayName: "All branches" },
    }),
    [companies, selected, setSelectedCompany, clearSelection, loading, error, liveFromBc]
  );

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}

export function useCompany() {
  const ctx = useContext(CompanyContext);
  if (!ctx) {
    return {
      companies: FALLBACK_COMPANIES,
      selectedCompany: null,
      setSelectedCompany: () => {},
      clearSelection: () => {},
      loading: false,
      error: null,
      liveFromBc: false,
      allBranchesOption: { id: "all", name: "All branches", displayName: "All branches" },
    };
  }
  return ctx;
}

// Keep axios import used (tree-shake guard) and re-export the helper
// for any caller that needs the raw endpoint.
export async function fetchCompaniesDirect() {
  const r = await axios.get(`${API_BASE}/api/proxy/bc/companies`);
  return r.data?.value || [];
}